import { useState, useEffect } from 'react';
import { Card, Button, Alert, Form, Modal } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { UserStatus } from '../utils/jwtUtils';

export const Login = () => {
    const {
        loginWithEmailPassword,
        registerWithEmailPassword,
        authError,
        authPending,
        user,
        getClaims,
    } = useAuth();
    const [mode, setMode] = useState('login');
    const [form, setForm] = useState({ email: '', password: '', confirm: '' });
    const [showStatusModal, setShowStatusModal] = useState(false);
    const isRegister = mode === 'register';

    // Check user status after login/register
    useEffect(() => {
        const checkUserStatus = async () => {
            if (!user) {
                return;
            }

            try {
                // Get claims to check user status
                const claims = await getClaims(false);
                if (claims && typeof claims.status === 'number') {
                    // If status is not Active (1), show warning modal
                    if (claims.status !== UserStatus.Active) {
                        setShowStatusModal(true);
                    }
                }
            } catch (error) {
                console.error('Error checking user status:', error);
            }
        };

        checkUserStatus();
    }, [user, getClaims]);

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!form.email || !form.password) {
            return;
        }

        if (isRegister && form.password !== form.confirm) {
            return alert('Las contraseñas no coinciden.');
        }

        try {
            if (isRegister) {
                await registerWithEmailPassword(form.email, form.password);
            } else {
                await loginWithEmailPassword(form.email, form.password);
            }
            
            // Check status immediately after successful login/register
            // Do this before component unmounts
            try {
                const claims = await getClaims(false);
                if (claims && typeof claims.status === 'number') {
                    // If status is not Active (1), show warning modal
                    if (claims.status !== UserStatus.Active) {
                        setShowStatusModal(true);
                    }
                }
            } catch (error) {
                console.error('Error checking user status after login:', error);
            }
        } catch (error) {
            // Error is already handled by authError
            console.error('Login/Register error:', error);
        }
    };

    return (
        <div className="d-flex justify-content-center mt-5">
            <Card style={{ maxWidth: 420 }} className="w-100">
                <Card.Body>
                    <Card.Title className="mb-3">Bienvenido a Academy Campus</Card.Title>
                    <Card.Text>
                        {isRegister
                            ? 'Crea tu cuenta usando tu correo corporativo para acceder al portal.'
                            : 'Inicia sesión con tu correo y contraseña para continuar.'}
                    </Card.Text>

                    {authError && (
                        <Alert variant="danger" className="mt-3">
                            {authError}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit} className="mt-3">
                        <Form.Group className="mb-3" controlId="loginEmail">
                            <Form.Label>Correo electrónico</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="usuario@academy.com"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3" controlId="loginPassword">
                            <Form.Label>Contraseña</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="********"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                required
                                minLength={6}
                            />
                        </Form.Group>
                        {isRegister && (
                            <Form.Group className="mb-3" controlId="loginConfirm">
                                <Form.Label>Confirmar contraseña</Form.Label>
                                <Form.Control
                                    type="password"
                                    placeholder="Repite tu contraseña"
                                    value={form.confirm}
                                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                                    required
                                    minLength={6}
                                />
                            </Form.Group>
                        )}

                        <Button
                            variant="primary"
                            className="w-100"
                            type="submit"
                            disabled={authPending}
                        >
                            {authPending
                                ? 'Procesando...'
                                : isRegister
                                ? 'Crear cuenta'
                                : 'Iniciar sesión'}
                        </Button>
                    </Form>

                    <Button
                        variant="link"
                        className="w-100 mt-2"
                        onClick={() => setMode(isRegister ? 'login' : 'register')}
                    >
                        {isRegister
                            ? 'Ya tengo una cuenta, iniciar sesión'
                            : 'Crear una cuenta nueva'}
                    </Button>
                </Card.Body>
            </Card>

            {/* Status Warning Modal */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Usuario no activo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning" className="mb-0">
                        El usuario no está activo. Consulte a su administrador.
                    </Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowStatusModal(false)}>
                        Entendido
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

